import React from 'react';
import { MessageSquare } from 'lucide-react';
import type { TaskComment } from '../../../types';
import type { TaskMode } from '../../../taskTypes/module';
import { CommentColumn } from '../CommentColumn';
import { VerificationColumn } from '../VerificationColumn';
import type { ValidatorStatus } from '../taskShellTypes';

/**
 * The task shell's side rail is a REGISTRY, not a union.
 *
 * Adding the verification rail took 23 files (#471) and the follow-up that
 * added one boolean and a counter took 6 more (#475), because a panel was a
 * hardcoded `'comments' | 'verification'` union repeated in seven places plus
 * a prop threaded through four layers. A panel is now one descriptor here and
 * one component; the union, the header toggles, the in-rail tabs, the
 * sessionStorage preference, and the open-panel request all derive from this
 * array.
 *
 * Order is the display order of the toggles and the tabs.
 */
export interface TaskShellPanelContext {
  mode: TaskMode;
  isAdminViewer: boolean;
  /** Root + reply comments already scoped to this task's fields. */
  fieldComments: TaskComment[];
  /** Root comments still unaddressed in the current round. */
  unresolvedRootCount: number;
  currentRound: number;
  validatorEnabled: boolean;
  canRunValidation: boolean;
  validatorStatus: ValidatorStatus;
  validatorError: string | null;
  validatorVisibleCount: number;
  /** First form-level blocker, shown before advisory AI findings. */
  submissionValidation: string | null;
}

export interface TaskShellPanel {
  id: string;
  /** Aside `aria-label`, and the noun in "Close {title}". */
  title: string;
  /** Header toggle + in-rail tab label. */
  label: string;
  /** Optional icon for the in-rail tab. */
  Icon?: React.ComponentType<{ className?: string }>;
  /** Whether this panel is offered at all for the current task. */
  availability: (context: TaskShellPanelContext) => boolean;
  /** Badge number shown on the header toggle and the tab. 0 hides it. */
  badgeCount: (context: TaskShellPanelContext) => number;
  Component: React.ComponentType<{ context: TaskShellPanelContext }>;
}

export const TASK_SHELL_PANELS: readonly TaskShellPanel[] = [
  {
    id: 'comments',
    title: 'Comments',
    label: 'Comments',
    Icon: MessageSquare,
    // Preview mode only shows it to admins (auditing read-only). Reviewers
    // can open the empty rail to start a thread via the field pins.
    // Annotators (`work` mode) are the exception: with no comments there is
    // nothing to act on, so the whole comments UI stays hidden until a
    // reviewer leaves the first one.
    availability: (context) => (context.mode !== 'preview' || context.isAdminViewer)
      && (context.mode !== 'work' || context.fieldComments.length > 0),
    badgeCount: (context) => context.unresolvedRootCount,
    Component: ({ context }) => (
      <CommentColumn
        mode={context.mode}
        comments={context.fieldComments}
        currentRound={context.currentRound}
      />
    ),
  },
  {
    id: 'verification',
    title: 'Validation',
    label: 'Validation',
    // Opt-in, so an untouched task does not open an empty rail. The status
    // leaves `idle` as soon as the button is pressed and stays visible for
    // progress, results, and retry. Findings can also arrive WITHOUT the
    // campaign toggle — a submit refused for AI-detected content returns
    // them — so the rail must show whenever there is something to show.
    availability: (context) => (
      (context.validatorEnabled && context.canRunValidation) || context.validatorVisibleCount > 0
    ) && context.mode !== 'preview' && context.validatorStatus !== 'idle',
    badgeCount: (context) => context.validatorVisibleCount,
    Component: ({ context }) => (
      <VerificationColumn
        validatorStatus={context.validatorStatus}
        validatorError={context.validatorError}
        submissionValidation={context.submissionValidation}
      />
    ),
  },
];

/**
 * The panel-id union, derived. This is the ONLY place it is spelled out; a
 * grep gate in `tools/taskTypeHostBoundary.test.js` keeps it that way.
 */
export type TaskShellPanelId = (typeof TASK_SHELL_PANELS)[number]['id'];

export function availablePanels(
  context: TaskShellPanelContext,
  panels: readonly TaskShellPanel[] = TASK_SHELL_PANELS,
): TaskShellPanel[] {
  return panels.filter((panel) => panel.availability(context));
}

/**
 * Resolve the panel to show: the requested one when it is still available,
 * otherwise the first available panel, otherwise none.
 *
 * Keeping this pure is what lets the "a third panel needs zero edits to
 * index.tsx" test assert against the selection rule directly.
 */
export function resolveActivePanel(
  requestedId: string | null,
  available: readonly TaskShellPanel[],
): TaskShellPanel | null {
  if (available.length === 0) return null;
  return available.find((panel) => panel.id === requestedId) ?? available[0];
}
